import React from 'react';
import PropTypes from 'prop-types';
import { Container } from 'semantic-ui-react';

import ArticleIcon from './ArticleIcon';
import TagLinks from './TagLinks';
import {
  discoveryLinks,
  formatDate,
  isPublishedNow,
  itemDate,
  itemTags,
  itemUrl,
} from './utils';

const paramsFromLocation = (location) =>
  new URLSearchParams(location?.search || '');

const labelForSubject = (subject) =>
  discoveryLinks.find((link) => link.tag === subject)?.label || subject;

const ArticleSearchPage = ({ location }) => {
  const params = paramsFromLocation(location);
  const text = params.get('SearchableText') || '';
  const subject = params.get('Subject') || '';
  const heading = subject ? labelForSubject(subject) : text;
  const [state, setState] = React.useState({
    error: '',
    items: [],
    loading: true,
    total: 0,
  });

  React.useEffect(() => {
    const controller = new AbortController();
    const apiParams = new URLSearchParams({
      b_size: '25',
      portal_type: 'Document',
    });
    ['Subject', 'effective', 'created', 'getPath'].forEach((field) => {
      apiParams.append('metadata_fields', field);
    });

    if (text) apiParams.set('SearchableText', text);
    if (subject) apiParams.set('Subject', subject);

    setState((current) => ({ ...current, error: '', loading: true }));

    fetch(`/++api++/@search?${apiParams.toString()}`, {
      headers: { Accept: 'application/json' },
      signal: controller.signal,
    })
      .then((response) => {
        if (!response.ok) throw new Error('Search failed');
        return response.json();
      })
      .then((data) => {
        setState({
          error: '',
          items: data.items || [],
          loading: false,
          total: data.items_total || 0,
        });
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        setState({ error: '検索に失敗しました。', items: [], loading: false, total: 0 });
      });

    return () => controller.abort();
  }, [location.search, subject, text]);

  const visibleItems = state.items.filter(isPublishedNow);

  return (
    <Container id="page-search">
      <article id="content">
        <header>
          <h1 className="documentFirstHeading">
            {heading ? `「${heading}」の記事` : '記事を探す'}
          </h1>
          {subject ? (
            <p className="description">
              タグ「{heading}」が付いた記事を表示します。
            </p>
          ) : (
            <p className="description">キーワード検索の結果です。</p>
          )}
          {!state.loading && !state.error && (
            <div className="items_total">{visibleItems.length}件の記事</div>
          )}
        </header>

        {state.loading && <p>検索しています...</p>}
        {state.error && <p>{state.error}</p>}
        {!state.loading && !state.error && !visibleItems.length && (
          <p>該当する記事はまだありません。</p>
        )}

        <section id="content-core" className="ehon-card-grid ehon-search-grid">
          {visibleItems.map((item, index) => (
            <article
              className={`listing-item ehon-card ${
                index === 0 ? 'ehon-card-featured' : ''
              }`}
              key={item['@id']}
            >
              <a href={itemUrl(item)}>
                <div className="listing-body ehon-card-body">
                  <ArticleIcon item={item} />
                  <span className="ehon-card-date">
                    {formatDate(itemDate(item)) || '読書記録'}
                  </span>
                  <h3>
                    <span className="ehon-card-mark">›</span>{' '}
                    {item.title ? item.title : item.id}
                  </h3>
                  <TagLinks tags={itemTags(item)} compact />
                  {item.description && <p>{item.description}</p>}
                </div>
              </a>
            </article>
          ))}
        </section>
      </article>
    </Container>
  );
};

ArticleSearchPage.propTypes = {
  location: PropTypes.shape({
    search: PropTypes.string,
  }).isRequired,
};

export default ArticleSearchPage;
